import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { useBreakpoints } from '@hub/hooks/useBreakpoints';
import { useState } from 'react';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import { Modal } from '@hub/common/Modal/Modal';
import { ProductMediaCarouselDisplayType, ProductMediaModalCarousel } from './ProductMediaModalCarousel';
import type { FadeCarouselSizeDefinition } from './ProductMediaFadeCarousel';
import { ProductMediaFadeCarousel } from './ProductMediaFadeCarousel';
import { ProductMediaSwipeCarousel } from './ProductMediaSwipeCarousel';
import { ProductMediaThumbCarousel } from './ProductMediaThumbCarousel';
import { ProductMediaThumbs } from './ProductMediaThumbs';

export interface ProductMediaCarouselProps {
  media: WidenMediaElement[];
  displayType?: ProductMediaCarouselDisplayType;
  showModelViewer?: boolean;
  mediaCarouselSize?: FadeCarouselSizeDefinition;
  onShowModalViewer?: () => void;
}

export const ProductMediaCarousel = ({
  media,
  displayType = ProductMediaCarouselDisplayType.Default,
  showModelViewer,
  mediaCarouselSize,
  onShowModalViewer,
}: ProductMediaCarouselProps) => {
  const { sm } = useBreakpoints();
  const [showModal, setShowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  let productMediaCarouselTemplate;

  if (media.length <= 1) {
    productMediaCarouselTemplate = (
      <div
        data-testid="product-image-single"
        className="flex flex-col justify-center items-center h-full cursor-pointer relative"
        onClick={() => setShowModal(true)}
      >
        {media.length === 1 && (
          <WidenImage
            width={sm ? 280 : 220}
            height={sm ? 210 : 165}
            alt={media[0].description || media[0].title}
            shortCode={media[0].shortCode}
            filename={media[0].filename}
            layout="fixed"
            title={media[0].title}
            priority={media[0].priority}
          />
        )}
      </div>
    );
  } else {
    productMediaCarouselTemplate = (
      <ProductMediaSwipeCarousel
        activeIndex={activeIndex}
        media={media}
        onClick={() => setShowModal(true)}
        onChange={idx => setActiveIndex(idx)}
      />
    );
    if (sm) {
      productMediaCarouselTemplate = (
        <>
          <ProductMediaFadeCarousel
            activeIndex={activeIndex}
            media={media}
            onClick={() => setShowModal(true)}
            onChange={idx => setActiveIndex(idx)}
            displayType={displayType}
            size={mediaCarouselSize}
          />
          {media.length > 3 ? (
            <ProductMediaThumbCarousel activeIndex={activeIndex} media={media} onChange={idx => setActiveIndex(idx)} />
          ) : (
            <ProductMediaThumbs activeIndex={activeIndex} media={media} onChange={idx => setActiveIndex(idx)} />
          )}
        </>
      );
    }
  }

  return (
    <div
      className="relative h-full text-center max-w-full p-4 border border-gray-200"
      data-testid="product-media-carousel"
    >
      {productMediaCarouselTemplate}
      <div className="absolute bottom-4 right-4 flex space-x-2">
        <div className="cursor-pointer" onClick={() => setShowModal(true)}>
          <Icon size={10} id={IconID.MAGNIFIER} primary={false} />
        </div>
        {showModelViewer && (
          <div className="cursor-pointer" onClick={onShowModalViewer}>
            <Icon size={10} id={IconID.THREE_DIMENSIONAL_MODEL} primary={false} />
          </div>
        )}
      </div>

      <Modal visible={showModal} onClose={() => setShowModal(false)}>
        <ProductMediaModalCarousel activeIndex={activeIndex} media={media} onChange={idx => setActiveIndex(idx)} />
      </Modal>
    </div>
  );
};
